import React, { useState } from "react";
import { CreditCard, Landmark, ShieldCheck, Loader2 } from "lucide-react";
import Modal from "@/components/chat/Modal";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const fmtCard = (s) => onlyDigits(s).slice(0, 19).replace(/(.{4})/g, "$1 ").trim();
const fmtExp = (s) => {
  const d = onlyDigits(s).slice(0, 4);
  return d.length >= 3 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

export default function PayoutModal({ existing, onClose, onSubmit }) {
  const { t } = useI18n();
  const [method, setMethod] = useState(existing?.type || "card");
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState({ card_number: "", exp: "", cvv: "", card_name: "" });
  const [bank, setBank] = useState({ account_name: "", account_number: "", routing_number: "", bank_name: "" });

  const cardValid = onlyDigits(card.card_number).length >= 13 && onlyDigits(card.exp).length === 4 && /^\d{3,4}$/.test(card.cvv) && card.card_name.trim();
  const bankValid = onlyDigits(bank.account_number).length >= 4 && onlyDigits(bank.routing_number).length >= 4 && bank.account_name.trim() && bank.bank_name.trim();
  const valid = method === "card" ? cardValid : bankValid;

  const submit = async () => {
    if (!valid) return toast.error(t("m_payout_fill"));
    setBusy(true);
    try {
      const payload = method === "card"
        ? { method: "card", card_number: onlyDigits(card.card_number), exp: onlyDigits(card.exp), cvv: card.cvv, card_name: card.card_name.trim() }
        : { method: "bank", account_name: bank.account_name.trim(), account_number: onlyDigits(bank.account_number), routing_number: onlyDigits(bank.routing_number), bank_name: bank.bank_name.trim() };
      await onSubmit(payload);
    } catch (e) { toast.error(e?.response?.data?.detail || t("m_payout_fail")); }
    finally { setBusy(false); }
  };

  const inp = "w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50";

  return (
    <Modal title={t("m_payout_title")} onClose={onClose}>
      <div className="space-y-4" data-testid="payout-modal">
        <div className="flex items-start gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-3">
          <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-200">{t("m_payout_receive_note")}</p>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setMethod("card")} data-testid="payout-method-card"
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium border ${method === "card" ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}><CreditCard size={16} /> {t("m_payout_card")}</button>
          <button onClick={() => setMethod("bank")} data-testid="payout-method-bank"
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium border ${method === "bank" ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}><Landmark size={16} /> {t("m_payout_bank")}</button>
        </div>

        {method === "card" ? (
          <div className="space-y-2.5">
            <div>
              <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_card_name")}</label>
              <input value={card.card_name} onChange={(e) => setCard({ ...card, card_name: e.target.value })} placeholder="Amara Diallo" className={inp} data-testid="payout-card-name" />
            </div>
            <div>
              <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_card_number")}</label>
              <input inputMode="numeric" value={card.card_number} onChange={(e) => setCard({ ...card, card_number: fmtCard(e.target.value) })} placeholder="4242 4242 4242 4242" className={inp} data-testid="payout-card-number" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_exp")}</label>
                <input inputMode="numeric" value={card.exp} onChange={(e) => setCard({ ...card, exp: fmtExp(e.target.value) })} placeholder="MM/YY" className={inp} data-testid="payout-card-exp" />
              </div>
              <div>
                <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_cvv")}</label>
                <input inputMode="numeric" value={card.cvv} onChange={(e) => setCard({ ...card, cvv: onlyDigits(e.target.value).slice(0, 4) })} placeholder="123" className={inp} data-testid="payout-card-cvv" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            <div>
              <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_acct_name")}</label>
              <input value={bank.account_name} onChange={(e) => setBank({ ...bank, account_name: e.target.value })} placeholder="Amara Diallo" className={inp} data-testid="payout-acct-name" />
            </div>
            <div>
              <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_bank_name")}</label>
              <input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="Ecobank" className={inp} data-testid="payout-bank-name" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_acct_number")}</label>
                <input inputMode="numeric" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: onlyDigits(e.target.value).slice(0, 20) })} placeholder="0001234567" className={inp} data-testid="payout-acct-number" />
              </div>
              <div>
                <label className="text-xs text-[var(--rc-muted)]">{t("m_payout_routing")}</label>
                <input inputMode="numeric" value={bank.routing_number} onChange={(e) => setBank({ ...bank, routing_number: onlyDigits(e.target.value).slice(0, 12) })} placeholder="110000000" className={inp} data-testid="payout-routing" />
              </div>
            </div>
          </div>
        )}

        <button onClick={submit} disabled={!valid || busy} data-testid="payout-submit"
          className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={17} />} {t("m_payout_save")}
        </button>
        <p className="text-[11px] text-center text-[var(--rc-muted)]">{t("m_payout_secure_note")}</p>
      </div>
    </Modal>
  );
}
